import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRecoilState, useRecoilValue } from "recoil";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Box, Drawer, FormControl, IconButton, InputAdornment, OutlinedInput, useMediaQuery, Button, Typography, CircularProgress, Tooltip, Dialog, DialogContent, DialogActions } from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { FC, KeyboardEventHandler, useState, useRef, useEffect, useMemo, useCallback } from "react";

import { findInstruments, fromFindInstrument, searchOmni } from "@/controllers/find-instruments";
import { isOmnisearchEnabled, searchDelayMs } from "@/lib/omnisearch/flag";
import { FindInstruments } from "@/models/find-instruments";
import HeaderAutocomplete from "@/components/header-autocomplete/HeaderAutocomplete";
import OmniAutocomplete, { routeForResult } from "@/components/omni-autocomplete/OmniAutocomplete";
import { recordSearchClick } from "@/lib/omnisearch/click";
import { OmniResult } from "@/lib/omnisearch/types";
import UniverseSelect from "@/components/universe-select/UniverseSelect";
import { themeState, userState, backgroundColorState, greyDarkerState, whiteMediumState, textColorState, greyLighterState, ortexColorState, chatMinimizedState, chatModalState } from "@/lib/store";
import Menu from "../menu/Menu";
import UserMenu from "../user-menu/UserMenu";
import Notifications from "../notifications/Notifications";
import HeaderCustomViews from "../header-custom-views/HeaderCustomViews";

import styles from "./Header.module.scss";
import chatInputStyles from "../chat-input/ChatInput.module.scss";
import { Stock } from "@/models/stock";
import { classNames } from "@/lib/utils";
import { getPreferredTabSuffix, readPreferredStockTab } from "@/lib/stock-tabs";
import { TextLoop } from "react-text-loop-next";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";
import dynamic from "next/dynamic";
import { useTradingVisible } from "@/lib/trading/useTradingAccess";

// Only builds with trading compile the buying-power strip; elsewhere the condition is a constant
// false and the import, with its Vertex calls, is dropped (see next.config.js).
const QuickStats = process.env.NEXT_PUBLIC_TRADING_ENABLED === "1" ? dynamic(() => import("@/components/trading/quick-stats/QuickStats")) : () => null;

interface PropsTypes {
    ticker: string;
    exchange: string;
    stock?: Stock;
    scrolledDown: boolean;
}

type Anchor = "top" | "left" | "bottom" | "right";

const Header: FC<PropsTypes> = ({ ticker, exchange, stock, scrolledDown }) => {
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();
    // Trading buying-power strip: staff in the trading group, on a trading build only (src/lib/trading/access.ts).
    const tradingVisible = useTradingVisible();

    const textColor = useRecoilValue(textColorState);
    const greyLighter = useRecoilValue(greyLighterState);
    const theme = useRecoilValue(themeState);
    const ortexColor = useRecoilValue(ortexColorState);
    const greyDarker = useRecoilValue(greyDarkerState);
    const whiteMedium = useRecoilValue(whiteMediumState);
    const backgroundColor = useRecoilValue(backgroundColorState);
    const user = useRecoilValue(userState);
    const [searchHistory, setSearchHistory] = useState<FindInstruments[]>([]);
    const [state, setState] = useState({
        top: false,
        left: false,
        bottom: false,
        right: false,
    });

    const isMobile = useMediaQuery("(max-width: 768px)");
    const isTablet = useMediaQuery("(max-width: 1024px)");
    const is4k = useMediaQuery("(min-width: 3840px)");

    const [searchBox, setSearchBox] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<number>(null);
    const [instruments, setInstruments] = useState<FindInstruments[]>([]);
    // Rows for the rich omnisearch dropdown. `null` means this query is on the legacy dropdown.
    const [omniResults, setOmniResults] = useState<OmniResult[] | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const [isVisitor, setIsVisitor] = useState<boolean>();
    const [showPlaceholder, setShowPlaceholder] = useState(true);
    const searchInputRef = useRef<HTMLInputElement>();
    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    // The query in the box right now, so a late reply for an earlier keystroke is dropped.
    const latestQueryRef = useRef<string>("");
    const focusTimeoutRef = useRef<NodeJS.Timeout>();
    const focusTimeout2Ref = useRef<NodeJS.Timeout>();
    const blurTimeoutRef = useRef<NodeJS.Timeout>();

    const { push } = useRouter();
    const router = useRouter();

    const [chatModal, setChatModal] = useRecoilState(chatModalState);
    const [isChatMinimized, setIsChatMinimized] = useRecoilState(chatMinimizedState);
    const [showMinimizedTooltip, setShowMinimizedTooltip] = useState(false);
    const [loginRequiredOpen, setLoginRequiredOpen] = useState(false);

    const placeholder = useMemo(
        () =>
            ticker && exchange ? (
                `${exchange}:${ticker}`
            ) : (
                <div className={styles.placeholderText}>
                    <Typography variant="subtitle2" color={textColor}>
                        Search{" "}
                    </Typography>
                    &nbsp;
                    <TextLoop>
                        <Typography variant="subtitle2">
                            <span className={styles.textLoopText}>ORTEX</span>
                        </Typography>
                        <Typography variant="subtitle2" color={textColor}>
                            <span>Stocks</span>
                        </Typography>
                        <Typography variant="subtitle2" color={textColor}>
                            <span>ETF's</span>
                        </Typography>
                        <Typography variant="subtitle2" color={textColor}>
                            <span>Tickers</span>
                        </Typography>
                    </TextLoop>
                </div>
            ),
        [ticker, exchange, textColor]
    );

    useEffect(() => {
        if (user["subscription_plan"] === "ORTEX Visitor") {
            setIsVisitor(true);
        } else {
            setIsVisitor(false);
        }
    }, [user]);

    const handleUpgradeClick = () => {
        trackUpgradeClick(UPGRADE_LOCATIONS.HEADER, { plan: user?.subscription_plan });
        return user.subscription_plan === "ORTEX Visitor" ? push("/login?register=true") : handleAppSubscriberUpgrade();
    };

    const upgradeButtonText = {
        "ORTEX Free": "Upgrade",
        "ORTEX Basic": "Get Advanced",
        "ORTEX Visitor": "Create free account",
    };

    useEffect(() => {
        setSearchHistory(user?.settings?.search_settings.map((instrument) => ({ ...instrument, is_history: true })));
    }, [user?.settings?.search_settings]);

    const omniHistory = useMemo(() => (searchHistory ?? []).map(fromFindInstrument), [searchHistory]);
    const searchSource = isTablet ? "header-mobile" : "header";

    const closeSearch = () => {
        setSearchBox(false);
        setActiveIndex(null);
        searchInputRef.current?.blur();
    };

    // Runs from onChange with the text as it now stands. It used to run from onKeyDown,
    // which fires before the letter lands in the box: typing "volvo" searched "", "v" and
    // "volv" but never "volvo", so the dropdown showed the answer for "volv" until another
    // key event came along (measured on app.ortex.com, 2026-09-13).
    const getInstruments = (query: string) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        latestQueryRef.current = query;
        if (!query.trim()) {
            // An empty box shows history; it does not search for nothing.
            setLoading(false);
            setInstruments(searchHistory);
            setOmniResults(isOmnisearchEnabled() && omniHistory.length ? omniHistory : null);
            return;
        }
        setLoading(true);
        searchTimeoutRef.current = setTimeout(() => {
            searchOmni(query).then((omni) => {
                // With the shorter omnisearch wait, a reply for "tes" can land after the
                // one for "tesla". Only the reply for what is in the box now may render.
                if (isOmnisearchEnabled() && latestQueryRef.current !== query) return;
                if (omni) {
                    setOmniResults(omni);
                    setInstruments([]);
                    if (omni.length) setActiveIndex(0);
                    setLoading(false);
                    return;
                }
                // Switch off, cold worker, error or no rows: the legacy dropdown, unchanged.
                setOmniResults(null);
                findInstruments(query).then((data) => {
                    if (isOmnisearchEnabled() && latestQueryRef.current !== query) return;
                    setInstruments([...data, ...searchHistory]);
                    if (data.length) setActiveIndex(0);
                    setLoading(false);
                });
            });
        }, searchDelayMs());
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
        switch (event.key) {
            case "Enter":
                if (omniResults && omniResults.length > 0 && activeIndex !== null) {
                    const selected = omniResults[activeIndex];
                    recordSearchClick(selected, latestQueryRef.current, activeIndex, searchSource);
                    push(routeForResult(selected, user?.settings));
                    closeSearch();
                } else if (instruments.length > 0 && activeIndex !== null) {
                    const selected = instruments[activeIndex];
                    const tabSuffix = getPreferredTabSuffix(readPreferredStockTab(user?.settings), selected.security_type, selected.exchange);
                    push(`/s/${selected.exchange || " ‎"}/${selected.ticker}${tabSuffix}`);
                    setSearchBox(false);
                    setActiveIndex(null);
                    searchInputRef.current?.blur();
                }
                break;
            case "ArrowDown": {
                const count = omniResults ? omniResults.length : instruments.length;
                if (count > 0 && activeIndex === null) {
                    setActiveIndex(0);
                } else if (count > 0 && activeIndex !== null) {
                    setActiveIndex(activeIndex !== count - 1 ? activeIndex + 1 : 0);
                }
                break;
            }
            case "ArrowUp":
                if ((omniResults ? omniResults.length : instruments.length) > 0 && activeIndex !== null) {
                    setActiveIndex(activeIndex !== 0 ? activeIndex - 1 : null);
                }
                break;
            default:
                setSearchBox(true);
                setActiveIndex(null);
                break;
        }
    };

    const handleInputFocus = useCallback(() => {
        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current);
        }
        if (focusTimeout2Ref.current) {
            clearTimeout(focusTimeout2Ref.current);
        }

        focusTimeoutRef.current = setTimeout(() => {
            setSearchBox(true);
            setShowPlaceholder(false);
        }, 250);

        focusTimeout2Ref.current = setTimeout(() => {
            setInstruments(searchHistory);
            setOmniResults(isOmnisearchEnabled() && omniHistory.length ? omniHistory : null);
        }, 500);
    }, [searchHistory, omniHistory]);

    const handleInputBlur = useCallback(() => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }

        blurTimeoutRef.current = setTimeout(() => {
            setSearchValue("");
            setSearchBox(false);
            setInstruments([]);
            setOmniResults(null);
            setShowPlaceholder(true);
        }, 250);
    }, []);

    const toggleDrawer = (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
            return;
        }
        setSearchBox(true);
        setState({ ...state, [anchor]: open });
    };

    useEffect(() => {
        if (isTablet || isMobile) {
            const handleRouteChange = () => {
                handleInputBlur();
                setState((prevState) => ({ ...prevState, top: false }));
            };

            router.events.on("routeChangeStart", handleRouteChange);

            return () => {
                router.events.off("routeChangeStart", handleRouteChange);
            };
        }
    }, [isMobile, isTablet, handleInputBlur]);

    useEffect(() => {
        if (isChatMinimized) {
            setShowMinimizedTooltip(true);
            const timeout = setTimeout(() => {
                setShowMinimizedTooltip(false);
            }, 3000); // Show for 3 seconds

            return () => clearTimeout(timeout);
        } else {
            setShowMinimizedTooltip(false);
        }
    }, [isChatMinimized]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current);
            }
            if (focusTimeout2Ref.current) {
                clearTimeout(focusTimeout2Ref.current);
            }
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const handleGoToLogin = useCallback(() => {
        setLoginRequiredOpen(false);
        router.push("/login");
    }, [router]);

    return (
        <>
            <header
                className={styles.Header}
                style={{
                    backgroundColor: isMobile && !scrolledDown ? "transparent" : backgroundColor,
                    visibility: user.email ? "visible" : "hidden",
                    borderBottom: isMobile ? "none" : `1px solid ${theme === "dark" ? greyDarker : theme === "light" ? whiteMedium : "unset"}`,
                    transition: "background-color 0.5s ease",
                }}
            >
                <div>
                    <Menu />
                    {isMobile ? (
                        <Box>
                            <Link href="/" passHref>
                                <IconButton className={styles.logo}>
                                    <Image unoptimized src="/assets/ortex-icon.png" alt="ortex-logo" width={20} height={20} priority />
                                </IconButton>
                            </Link>
                        </Box>
                    ) : (
                        <Box sx={{ cursor: "pointer" }}>
                            <Link href="/" passHref>
                                <a>
                                    <Image unoptimized src="/assets/ortex-logo-h.png" alt="ortex-logo-h" width={145} height={30} priority />
                                </a>
                            </Link>{" "}
                        </Box>
                    )}

                    <div className={classNames(styles.middleSection, searchBox ? styles.searchStarted : "")}>
                        <Box>
                            {isMobile || isTablet ? (
                                <div className={styles.mobileSearch}>
                                    <IconButton aria-label="search" style={{ color: textColor }} onClick={toggleDrawer("top", !state["top"])}>
                                        <SearchIcon />
                                    </IconButton>

                                    <Drawer
                                        anchor="top"
                                        open={state["top"]}
                                        onClose={toggleDrawer("top", false)}
                                        sx={{
                                            zIndex: 6,
                                            "& .MuiDrawer-paperAnchorTop": {
                                                top: `calc(40px + env(safe-area-inset-top) + ${is4k ? "40px" : "25px"})`,
                                            },
                                        }}
                                        className={styles.expo}
                                    >
                                        <div className={styles.mobileSearchDrawer}>
                                            <div className={styles.mobileSearchDrawerContent}>
                                                <FormControl size="small" fullWidth sx={{ mt: 1, mb: 1, display: "flex", flex: 1, width: "unset", border: 0 }}>
                                                    <OutlinedInput
                                                        type="text"
                                                        inputRef={searchInputRef}
                                                        onKeyDown={handleKeyDown}
                                                        onFocus={handleInputFocus}
                                                        autoCapitalize="none"
                                                        onChange={(event) => {
                                                            setSearchValue(event.target.value);
                                                            setSearchBox(true);
                                                            getInstruments(event.target.value);
                                                        }}
                                                        value={searchValue}
                                                        sx={{
                                                            width: "100%",
                                                        }}
                                                        id="outlined-adornment-amount"
                                                        className={styles.searchInput}
                                                        startAdornment={
                                                            <InputAdornment position="start">
                                                                <SearchIcon htmlColor={greyLighter} />
                                                            </InputAdornment>
                                                        }
                                                        autoComplete="off"
                                                        name="search"
                                                    />
                                                    {showPlaceholder && <div className={styles.placeholder}>{placeholder}</div>}
                                                </FormControl>
                                                {searchBox &&
                                        (omniResults ? (
                                            omniResults.length > 0 && (
                                                <OmniAutocomplete results={omniResults} activeIndex={activeIndex} query={latestQueryRef.current} source={searchSource} onMouseEnter={() => setActiveIndex(null)} onSelect={closeSearch} />
                                            )
                                        ) : (
                                            instruments.length > 0 && <HeaderAutocomplete instruments={instruments} activeIndex={activeIndex} onMouseEnter={() => setActiveIndex(null)} />
                                        ))}
                                                {loading && (
                                                    <div className={styles.loader}>
                                                        <CircularProgress size={15} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Drawer>
                                </div>
                            ) : (
                                <div className={styles.search}>
                                    <div className={styles.backdrop}></div>
                                    <FormControl size="small" fullWidth sx={{ mt: 1, mb: 1, display: "flex", flex: 1, width: "unset", border: 0 }}>
                                        <OutlinedInput
                                            id="outlined-adornment-amount"
                                            value={searchValue}
                                            className={styles.searchInput}
                                            onChange={(event) => {
                                                            setSearchValue(event.target.value);
                                                            setSearchBox(true);
                                                            getInstruments(event.target.value);
                                                        }}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <SearchIcon htmlColor={textColor} />
                                                </InputAdornment>
                                            }
                                            onKeyDown={handleKeyDown}
                                            onFocus={handleInputFocus}
                                            onBlur={handleInputBlur}
                                            autoCapitalize="none"
                                            autoComplete="off"
                                            name="search"
                                        />
                                        {showPlaceholder && (
                                            <div className={styles.placeholder}>
                                                <Typography variant="subtitle2"> {placeholder}</Typography>
                                            </div>
                                        )}
                                    </FormControl>
                                    {searchBox &&
                                        (omniResults ? (
                                            omniResults.length > 0 && (
                                                <OmniAutocomplete results={omniResults} activeIndex={activeIndex} query={latestQueryRef.current} source={searchSource} onMouseEnter={() => setActiveIndex(null)} onSelect={closeSearch} />
                                            )
                                        ) : (
                                            instruments.length > 0 && <HeaderAutocomplete instruments={instruments} activeIndex={activeIndex} onMouseEnter={() => setActiveIndex(null)} />
                                        ))}
                                </div>
                            )}
                        </Box>
                        <div className={styles.universe}>
                            <UniverseSelect />
                        </div>
                    </div>
                    {["ORTEX Free", "ORTEX Basic", "ORTEX Visitor"].includes(user?.subscription_plan) && !isMobile && (
                        <Button variant="outlined" size="small" onClick={handleUpgradeClick} sx={{ minWidth: "80px" }}>
                            {upgradeButtonText[user?.subscription_plan]}
                        </Button>
                    )}
                    {tradingVisible && !isMobile && <QuickStats />}

                    <Tooltip {...(showMinimizedTooltip ? { open: true } : {})} arrow title={showMinimizedTooltip ? "Continue the conversation here" : "ORTEX AI"}>
                        <IconButton
                            aria-label="ORTEX AI"
                            className={styles.askOrtexAI}
                            style={{ color: textColor, marginLeft: "10px" }}
                            onClick={() => {
                                if (chatModal.open && !isChatMinimized) {
                                    // Chat is open and visible - close it
                                    setChatModal({ open: false, initialMessage: undefined, startingModel: undefined, chatModel: undefined });
                                } else if (chatModal.open && isChatMinimized) {
                                    // Chat is minimized - un-minimize it
                                    setIsChatMinimized(false);
                                } else {
                                    // Chat is closed - open it (or show login dialog for visitors)
                                    if (isVisitor) {
                                        setLoginRequiredOpen(true);
                                        return;
                                    }
                                    setChatModal({ open: true, initialMessage: undefined, startingModel: undefined, chatModel: undefined });
                                    setIsChatMinimized(false);
                                }
                            }}
                        >
                            <AutoAwesomeIcon color="action" style={{ color: textColor }} />
                        </IconButton>
                    </Tooltip>
                    {/* Phones had no way to sign up from the header at all (the button
                        above is desktop-only). A signed-out visitor has no custom views
                        and no notifications of their own, so on a phone the sign-up button
                        takes those two icons' slots (the row is already full at 375px). */}
                    {isVisitor && isMobile ? (
                        <Button variant="contained" size="small" onClick={handleUpgradeClick} sx={{ minWidth: 0, flexShrink: 0, px: 1.25, mx: 0.5, fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap", textTransform: "none" }}>
                            Sign up
                        </Button>
                    ) : (
                        <>
                            <HeaderCustomViews />
                            <Notifications />
                        </>
                    )}
                    <div className={styles.user}>
                        <UserMenu firstName={user.first_name} lastName={user.last_name} isVisitor={isVisitor} />
                    </div>
                </div>
            </header>

            <Dialog
                open={loginRequiredOpen}
                onClose={() => setLoginRequiredOpen(false)}
                PaperProps={{
                    className: classNames(chatInputStyles.loginDialogPaper, theme === "dark" && chatInputStyles.dark),
                }}
                slotProps={{ backdrop: { className: chatInputStyles.loginDialogBackdrop } }}
            >
                <DialogContent className={chatInputStyles.loginDialogContent} sx={{ color: textColor }}>
                    <Box className={chatInputStyles.loginDialogIconWrap} sx={{ "& .MuiSvgIcon-root": { color: ortexColor } }}>
                        <ChatBubbleOutlineRoundedIcon />
                    </Box>
                    <Typography variant="h6" className={chatInputStyles.loginDialogTitle}>
                        Sign in to chat
                    </Typography>
                    <Typography variant="body2" className={chatInputStyles.loginDialogBody}>
                        Create a free account or log in to chat with the ORTEX assistant and get answers instantly.
                    </Typography>
                </DialogContent>
                <DialogActions className={chatInputStyles.loginDialogActions}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleGoToLogin}
                        className={chatInputStyles.loginDialogPrimaryBtn}
                        sx={{ bgcolor: `${ortexColor} !important`, color: "#fff", "&:hover": { bgcolor: `${ortexColor} !important`, filter: "brightness(1.1)" } }}
                    >
                        Sign in
                    </Button>
                    <Button variant="text" onClick={() => setLoginRequiredOpen(false)} className={chatInputStyles.loginDialogSecondaryBtn} sx={{ color: textColor }}>
                        Not now
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Header;
